import PropTypes from 'prop-types';
import { useMemo, useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import { Paper, Divider, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { useResponsive } from 'src/hooks/use-responsive';
import { useLocalStorage } from 'src/hooks/use-local-storage';

import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';
import { useEntityContext } from 'src/entity/hooks';
import NavGlobal from 'src/layouts/dashboard/nav-global';
import { useGetAdvancedUserPermissions } from 'src/api/users-entities';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';
import MysubentitiesMenu from 'src/components/mysubentities-menu';
import MyEntitiesMenu from 'src/components/myentities-menu/myentities-menu';

import { NAV } from '../../config-layout';

// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav, navData, itemHref }) {
  const { t } = useTranslate();
  const {
    user: { id: user_id, role: user_role, isSuperadmin },
  } = useAuthContext();
  const pathname = usePathname();
  const lgUp = useResponsive('up', 'lg');
  const { state } = useLocalStorage('entity');
  const { entity } = useEntityContext();
  const { permissions, parentPermissions } = useGetAdvancedUserPermissions(user_id, entity?.id);

  const {
    myEntities,
    entity: { id, name, logo, parent, is_parent, entities: mySubEntities },
  } = useEntityContext();

  const selected_subentity = useMemo(() => {
    let _selected = mySubEntities.find((se) => `${id}` === `${se.id}`);
    if (!_selected) {
      _selected = mySubEntities.find((se) => `${state?.id}` === `${se.id}`);
    }
    if (!_selected) _selected = mySubEntities[0];
    return _selected;
  }, [mySubEntities, id, state]);

  const show_parent_nav = useMemo(
    () => is_parent || (parent && myEntities.find((my) => my.id === parent)),
    [is_parent, myEntities, parent]
  );

  const parentNavItems = useMemo(() => {
    const items = [
      {
        title: t('nav.menu.dashboard'),
        path: paths.entity.modules.ges.dashboard.root(parent || id),

        icon: <Iconify icon="material-symbols:dashboard-outline" />,
        disabled: false,
      },
    ];

    if (
      isSuperadmin ||
      (parentPermissions
        ? parentPermissions.is_admin || parentPermissions.can_access_ges
        : permissions?.can_access_ges)
    ) {
      items.push(
        {
          title: t('nav.menu.inventory'),
          path: paths.entity.modules.ges.inventory.root(parent || id),
          icon: <Iconify icon="mingcute:inventory-line" />,
        },
        {
          title: t('nav.menu.settings'),
          path: paths.entity.modules.ges.settings.root(parent || id),
          icon: <Iconify icon="lucide:settings" />,
        }
      );
    }

    return [
      {
        subheader: '',
        items,
      },
    ];
  }, [t, parent, id, isSuperadmin, parentPermissions, permissions?.can_access_ges]);

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          m: 2,
        }}
      >
        <MyEntitiesMenu
          size="small"
          sx={{
            textAlign: 'left',
            borderRadius: 1,
            fontSize: 11,
            cursor: 'pointer',
            typography: 'overline',
            display: 'block',
            color: 'text.disabled',
            width: '100%',
            transition: (theme) =>
              theme.transitions.create(['color'], {
                duration: theme.transitions.duration.shortest,
              }),
            '&:hover': {
              color: 'primary.contrastText',
            },
          }}
        >
          <Paper
            sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            variant="outlined"
          >
            <Box
              component="img"
              src={logo}
              alt={name}
              sx={{ width: '100%', height: 75, objectFit: 'contain' }}
            />
          </Paper>
        </MyEntitiesMenu>
      </Box>

      {show_parent_nav && (
        <Stack gap={2}>
          <Box sx={{ px: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                p: 1,
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight="bold" textTransform="uppercase">
                {t('nav.menu.subentities.parent_title')}
              </Typography>
            </Box>
          </Box>
          <NavSectionVertical
            data={parentNavItems}
            slotProps={{
              currentRole: user_role,
            }}
          />
          <>
            <Divider />

            <Box sx={{ px: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight="bold" textTransform="uppercase">
                  {t('nav.menu.subentities.title')}
                </Typography>
              </Box>
            </Box>

            <MysubentitiesMenu
              size="small"
              itemHref={itemHref}
              sx={{
                bgcolor: 'primary.lighter',
                borderRadius: 1,
                textAlign: 'left',
                fontSize: 11,
                cursor: 'pointer',
                typography: 'overline',
                display: 'block',
                p: 1,
                mx: 2,
                mb: 1,
                '&:hover': {
                  bgcolor: 'primary.light',
                },
                transition: (theme) =>
                  theme.transitions.create(['background-color'], {
                    duration: theme.transitions.duration.shortest,
                  }),
              }}
            >
              <Stack gap={1} direction="row" alignItems="center" justifyContent="space-around">
                <Stack direction="row" alignItems="center">
                  <Box
                    component="img"
                    alt={name}
                    src={selected_subentity?.logo || logo}
                    sx={{
                      width: 24,
                      height: 24,
                      mr: 1,
                      objectFit: 'contain',
                      bgcolor: 'white',
                      p: 0.25,
                    }}
                  />
                  {selected_subentity?.name || name}
                </Stack>

                <Iconify icon="mdi:menu-down" width={14} />
              </Stack>
            </MysubentitiesMenu>
          </>
        </Stack>
      )}

      <NavSectionVertical
        data={navData}
        slotProps={{
          currentRole: user_role,
        }}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Divider sx={{ mt: 2 }} />
      <NavGlobal />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `solid 1px ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            color: 'text.primary',
            boxShadow: (theme) => theme.shadows[6],
            zIndex: (theme) => theme.zIndex.appBar + 1,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
          sx={{
            '& .MuiDrawer-paper': {
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRight: (theme) => `solid 1px ${theme.palette.divider}`,
              boxShadow: (theme) => theme.shadows[6],
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

NavVertical.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
  navData: PropTypes.array,
  itemHref: PropTypes.func,
};
